/**
 * API contracts. NFR-Q-02: this is the ONE schema definition per contract —
 * the Fastify validator, the OpenAPI document and the SPA's types all derive
 * from these. There is no hand-maintained duplicate.
 */
import { z } from 'zod';
import { isMailboxSpec } from './mailbox.js';
import { MANUAL_LINK_TYPES } from './links.js';
import { TELEMETRY_LIMITS } from './telemetry.js';
import { outboundSettingsSchema } from './outboundCaps.js';
import { intakeSettingsSchema } from './intake.js';
import {
  ACTOR_SOURCES,
  CONVERSATION_KINDS,
  FIELD_REQUIRED_AT,
  FIELD_TYPES,
  FIELD_VISIBILITY,
  FIELD_WHEN_MISSING_BY_EMAIL,
  PENDING_STATES,
  TENANT_ROLES,
  TICKET_PRIORITIES,
  TICKET_STATES,
} from './types.js';

export const tenantKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{1,30}$/, 'tenant key must be a lowercase slug');

export const esaEmailSchema = z.string().email().max(254);

/** ESA project numbers: D202401044.00 / P202600322.00 (FR-WC-01). */
export const projectNumberSchema = z
  .string()
  .regex(/^[A-Z]\d{9}\.\d{2}$/, 'expected a project number like D202401044.00');

export const paginationSchema = z.object({
  cursor: z.string().optional(),
  /** NFR-P-06: every list endpoint has a default AND an enforced maximum. */
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type Pagination = z.infer<typeof paginationSchema>;

// ---------------------------------------------------------------------------
// Ticket creation
// ---------------------------------------------------------------------------

export const createTicketSchema = z.object({
  subject: z.string().min(1).max(500),
  body: z.string().max(100_000).default(''),
  requestType: z.string().max(120).optional(),
  queueKey: z.string().max(60).optional(),
  /**
   * FR-ID-02 / FR-API-04: the person the request is ABOUT, when that differs
   * from whoever submitted it. Onboarding, access and Publications tickets
   * routinely do, and an automation that assumes otherwise provisions the
   * wrong person.
   */
  subjectEmail: esaEmailSchema.optional(),
  /** On-behalf-of submission — who is asking (FR-PR-07, gated per tenant). */
  requesterEmail: esaEmailSchema.optional(),
  systems: z.array(z.string().max(80)).max(20).default([]),
  projectNumbers: z.array(projectNumberSchema).max(10).default([]),
  custom: z.record(z.string(), z.unknown()).default({}),
  tags: z.array(z.string().max(40)).max(20).default([]),
  requestedBy: z.coerce.date().optional(),
  source: z.enum(ACTOR_SOURCES).default('portal'),
  /**
   * A request the assistant already answered (FR-API-07).
   *
   * Its presence turns the create into a SOFT CLOSE: the ticket is filed and
   * immediately resolved, carrying the answer as its resolving reply and the
   * articles it was answered from. Accepted only with `source: 'assistant'`,
   * and only from the assistant's own integration credential — otherwise
   * anyone could file work as already done.
   */
  assistantAnswer: z
    .object({
      answer: z.string().min(1).max(20_000),
      /** What it answered from — ours or the firm's pre-existing KB. */
      cited: z
        .array(
          z.object({
            articleId: z.string().min(1).max(200),
            title: z.string().max(500).optional(),
            collection: z.string().max(120).optional(),
            score: z.number().min(0).max(1).optional(),
            retrieval: z.enum(['vector', 'lexical']).optional(),
          }),
        )
        .max(10)
        .optional(),
    })
    .optional(),
});
export type CreateTicketInput = z.infer<typeof createTicketSchema>;

// ---------------------------------------------------------------------------
// State transitions
// ---------------------------------------------------------------------------

/**
 * FR-AG-04: entering a pending_* state REQUIRES a reason, and
 * pending_external additionally requires the party being waited on. This is
 * the data that explains the 143-hour p90, so the schema refuses the
 * transition without it rather than accepting a blank.
 */
export const changeStateSchema = z
  .object({
    state: z.enum(TICKET_STATES),
    reason: z.string().min(1).max(1000).optional(),
    blockedParty: z.string().min(1).max(200).optional(),
    note: z.string().max(4000).optional(),
    /**
     * What actually fixed it (FR-KB-10). Required when resolving.
     *
     * Not the closing reply to the requester — that is correspondence and
     * usually says "all sorted now". This is the record for the next person
     * who hits the same problem, and without it every drafted article can
     * only ever be a demand brief.
     */
    fix: z.string().trim().min(4).max(4000).optional(),
    /** What it turned out to be, where that differs from the report. */
    cause: z.string().trim().max(2000).optional(),
    /**
     * Explicit, never defaulted (FR-KB-12). Decides whether a
     * requester-facing article is worth drafting alongside the internal one.
     */
    selfSolvable: z.boolean().optional(),
    /** Whether the fix text came from the model, and whether it was edited. */
    fixSource: z.enum(['agent', 'accepted_draft', 'edited_draft']).optional(),
    /**
     * Which configured event this ticket is waiting on, and until when.
     *
     * Only meaningful for `pending_event`. The date is sent by the caller
     * rather than derived from the event type's `defaultDays`, because the
     * default is a suggestion and the agent is the one who knows: "she is
     * back on the 3rd" is information no rule holds.
     */
    eventTypeKey: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/).optional(),
    eventDueAt: z.string().datetime({ offset: true }).optional(),
    /**
     * Custom field values supplied as part of closing (FR-CF-05).
     *
     * The close-phase check reads the values on the TICKET, so without this
     * a required-at-close field could only be satisfied by patching the
     * ticket first — which nothing did, making "Resolution code is required"
     * a rejection with no way to answer it.
     */
    custom: z.record(z.string(), z.unknown()).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.state === 'resolved') {
      if (!v.fix) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fix'],
          message: 'say what fixed it — this is what a future article is written from',
        });
      }
      if (v.selfSolvable === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['selfSolvable'],
          message: 'say whether the requester could have done this themselves',
        });
      }
    }
    const pending = (PENDING_STATES as readonly string[]).includes(v.state);
    if (pending && !v.reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reason'],
        message: `a reason is required when moving to ${v.state}`,
      });
    }
    if (v.state === 'pending_external' && !v.blockedParty) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['blockedParty'],
        message: 'blockedParty is required for pending_external',
      });
    }
    if (v.state === 'pending_event') {
      if (!v.eventTypeKey) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['eventTypeKey'],
          message: 'say which event this is waiting for',
        });
      }
      // WITHOUT A DATE NOTHING EVER WAKES IT. Every other pending state is
      // released by a person looking at the ticket; this one is released by
      // a sweep reading this column.
      if (!v.eventDueAt) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['eventDueAt'],
          message: 'say when to bring it back',
        });
      } else if (Number.isNaN(Date.parse(v.eventDueAt))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['eventDueAt'],
          message: 'that is not a date',
        });
      }
    }
  });
export type ChangeStateInput = z.infer<typeof changeStateSchema>;

export const patchTicketSchema = z
  .object({
    subject: z.string().min(1).max(500).optional(),
    /** Set by a person, never derived. `critical` pins it above everything. */
    priority: z.enum(TICKET_PRIORITIES).optional(),
    requestType: z.string().max(120).nullable().optional(),
    queueKey: z.string().max(60).optional(),
    assigneeOid: z.string().max(120).nullable().optional(),
    systems: z.array(z.string().max(80)).max(20).optional(),
    projectNumbers: z.array(projectNumberSchema).max(10).optional(),
    custom: z.record(z.string(), z.unknown()).optional(),
    tags: z.array(z.string().max(40)).max(20).optional(),
    requestedBy: z.coerce.date().nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'no fields to update');
export type PatchTicketInput = z.infer<typeof patchTicketSchema>;

export const addConversationSchema = z.object({
  kind: z.enum(CONVERSATION_KINDS).refine((k) => k === 'reply' || k === 'note', {
    message: 'only reply or note may be posted through the API',
  }),
  body: z.string().min(1).max(100_000),
  /**
   * WHO THE ENTRY IS FROM, when that is not the caller (FR-API-09).
   *
   * The assistant relays a requester's own message onto an existing ticket,
   * and without this every one of those was attributed to the API key — so a
   * ticket's history read as though the integration had said everything, and
   * the requester's words appeared under a service account. Visible to the
   * requester too, now that these are replies rather than notes.
   *
   * Same shape and same resolution as `requesterEmail` on create, and the
   * same rule: a machine caller may name somebody else, a person may not
   * unless they are staff. Absent means the caller, so nothing that worked
   * before changes.
   */
  authorEmail: esaEmailSchema.optional(),
  /**
   * How many files the page will upload against this entry once it exists.
   * A reply's email waits for them (then for the release call, or a few
   * minutes) instead of leaving without them.
   */
  files: z.number().int().min(0).max(50).optional(),
  /**
   * The suggested reply this one was started from (082), with the text as it
   * was put in the composer, so the send can say how much of it survived.
   * Recording only: a wrong or stale one is ignored, never refused.
   */
  suggestion: z.object({ id: z.string().uuid(), text: z.string().max(100_000) }).optional(),
});
export type AddConversationInput = z.infer<typeof addConversationSchema>;

export const transferSchema = z.object({
  toTenant: tenantKeySchema,
  toQueueKey: z.string().max(60).optional(),
  reason: z.string().min(1).max(1000),
  custom: z.record(z.string(), z.unknown()).default({}),
});
export type TransferInput = z.infer<typeof transferSchema>;

export const mergeSchema = z.object({
  intoTicketNumber: z.coerce.number().int().positive(),
  reason: z.string().min(1).max(1000),
  /**
   * Email the merged ticket's requester that it carries on elsewhere. On
   * unless the agent says otherwise: a cancelled ticket and silence leaves
   * them wondering where their request went.
   */
  tellRequester: z.boolean().default(true),
});

// ---------------------------------------------------------------------------
// Ticket list / search
// ---------------------------------------------------------------------------

export const ticketListQuerySchema = paginationSchema.extend({
  state: z
    .union([z.enum(TICKET_STATES), z.array(z.enum(TICKET_STATES))])
    .optional()
    .transform((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v])),
  /** Convenience bucket: everything not closed or cancelled. */
  open: z.coerce.boolean().optional(),
  queueKey: z.string().max(60).optional(),
  assigneeOid: z.string().max(120).optional(),
  unassigned: z.coerce.boolean().optional(),
  /** Held by somebody whose Outlook automatic replies say they are out (085). */
  assigneeAway: z.coerce.boolean().optional(),
  requestType: z.string().max(120).optional(),
  projectNumber: z.string().max(20).optional(),
  system: z.string().max(80).optional(),
  tag: z.string().max(40).optional(),
  /** FR-AG-01: SLA risk as a first-class filter. */
  slaRisk: z.enum(['at_risk', 'breached']).optional(),
  needsHumanRouting: z.coerce.boolean().optional(),
  q: z.string().max(200).optional(),
  sort: z
    .enum(['created_desc', 'created_asc', 'updated_desc', 'due_asc', 'number_desc'])
    .default('updated_desc'),
});
export type TicketListQuery = z.infer<typeof ticketListQuerySchema>;

// ---------------------------------------------------------------------------
// Configuration (FR-AD-01)
// ---------------------------------------------------------------------------

export const fieldDefinitionSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/),
  label: z.string().min(1).max(120),
  fieldType: z.enum(FIELD_TYPES),
  helpText: z.string().max(500).nullish(),
  validation: z
    .object({
      options: z.array(z.string().max(120)).max(200).optional(),
      min: z.number().optional(),
      max: z.number().optional(),
      pattern: z.string().max(500).optional(),
      maxLength: z.number().int().positive().optional(),
    })
    .default({}),
  requiredAt: z.enum(FIELD_REQUIRED_AT).default('never'),
  visibility: z.enum(FIELD_VISIBILITY).default('agent'),
  /** When a ticket comes in by email without it (061). */
  whenMissingByEmail: z.enum(FIELD_WHEN_MISSING_BY_EMAIL).default('leave'),
  requestTypeKeys: z.array(z.string().max(120)).nullish(),
  showWhen: z.object({ field: z.string(), equals: z.unknown() }).nullish(),
  /**
   * FR-CF-06: a field may not exist without a stated consumer — a report, a
   * routing rule, or an automation. This is the schema-level half of the rule
   * that stops the next VPN sub-category from being created.
   */
  consumer: z.string().min(3).max(200),
  sortOrder: z.number().int().default(0),
});
export type FieldDefinitionInput = z.infer<typeof fieldDefinitionSchema>;

export const routingRuleSchema = z.object({
  name: z.string().min(1).max(120),
  sortOrder: z.number().int(),
  predicate: z.object({
    all: z
      .array(
        z.object({
          field: z.enum([
            'request_type',
            'systems',
            'sender_email',
            'sender_domain',
            'requester_office',
            'requester_department',
            'template',
            'subject',
            'body',
            'project_number_present',
          ]),
          op: z.enum(['eq', 'neq', 'in', 'contains', 'matches', 'present', 'absent']),
          value: z.unknown().optional(),
        }),
      )
      .min(1),
  }),
  action: z.object({
    queueKey: z.string().max(60).optional(),
    assigneeOid: z.string().max(120).optional(),
    setFields: z.record(z.string(), z.unknown()).optional(),
    flagForHuman: z.boolean().optional(),
    /** How the ticket is handled (see core `handlingFor`): every matching rule counts. */
    priority: z.enum(TICKET_PRIORITIES).optional(),
    holdForPerson: z.boolean().optional(),
    keepOpen: z.boolean().optional(),
    notify: z
      .array(z.string().trim().toLowerCase().email('Each alert recipient must be an email address'))
      .max(10)
      .optional(),
  }),
});
export type RoutingRuleInput = z.infer<typeof routingRuleSchema>;

export const slaPolicySchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/),
  name: z.string().min(1).max(120),
  requestTypeKey: z.string().max(120).nullish(),
  calendarKey: z.string().min(1).max(60),
  firstResponseMins: z.number().int().positive(),
  resolutionMins: z.number().int().positive(),
  atRiskPct: z.number().int().min(1).max(99).default(80),
});

/**
 * Everything below is a WRITE shape for the configuration screens. They are
 * separate from the read shapes deliberately: a tenant admin may set a queue's
 * name and order, but never its id, and never another tenant's.
 */

/**
 * Creating a group.
 *
 * The id is permanent and appears in every URL, so it is validated hard and
 * cannot be changed afterwards — renaming it would orphan every ticket, rule,
 * field and report that references it. The display name is free text and is
 * the thing people actually read.
 */
export const createTenantSchema = z.object({
  id: tenantKeySchema,
  name: z.string().min(2).max(120),
  /** An IANA zone. The SLA clock is meaningless without the right one. */
  timezone: z.string().min(3).max(60).default('America/Los_Angeles'),
  /** Seed a starter queue and request types, so the group works immediately. */
  withStarterConfig: z.boolean().default(true),
});
export type CreateTenantInput = z.infer<typeof createTenantSchema>;

/**
 * A webhook subscription (FR-N8N-04).
 *
 * `scope` is explicit rather than inferred from whether a tenant id was sent:
 * "global" is a decision with blast radius — every group's ticket events go to
 * that URL — and a field somebody has to set on purpose is better than one
 * they get by omission.
 */
export const webhookSubscriptionSchema = z.object({
  name: z.string().min(2).max(80),
  url: z.string().url().max(2000).refine((u) => /^https?:\/\//i.test(u), 'must be http or https'),
  scope: z.enum(['tenant', 'global']).default('tenant'),
  /** Exact event types, families like `ticket.*`, or `*`. */
  events: z.array(z.string().min(1).max(60)).min(1).max(40),
  /** Static headers for a receiver with its own auth. X-ESA-* is reserved. */
  headers: z.record(z.string().max(80), z.string().max(1000)).default({}),
  active: z.boolean().default(true),
});
export type WebhookSubscriptionInput = z.infer<typeof webhookSubscriptionSchema>;

export const webhookUpdateSchema = webhookSubscriptionSchema.partial().omit({ scope: true });
export type WebhookUpdateInput = z.infer<typeof webhookUpdateSchema>;

/**
 * A thing this desk waits for (FR-LC "Move to Event").
 *
 * `defaultDays` is a suggestion the dialog prefills, never a rule — see 046.
 * Capped at ten years because a wait longer than that is a mistake somebody
 * should notice at the point of typing it, not when the ticket fails to
 * return in 2041.
 */
export const eventTypeSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/),
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullish(),
  defaultDays: z.number().int().min(1).max(3650).nullish(),
  sortOrder: z.number().int().default(0),
  active: z.boolean().default(true),
});
export type EventTypeInput = z.infer<typeof eventTypeSchema>;

/** Relating one ticket to another (FR "Move to Event"'s sibling: parentage). */
export const linkTicketSchema = z.object({
  /** The other ticket, by the number a person can actually read off a screen. */
  otherNumber: z.number().int().positive(),
  /** Which desk it lives on. Absent means this one. */
  otherTenant: z.string().min(1).max(64).optional(),
  type: z.enum(MANUAL_LINK_TYPES as unknown as [string, ...string[]]),
  /**
   * Whether THIS ticket is the parent.
   *
   * Only meaningful for `parent_of`, and it has to be asked: "link #200 to
   * this onboarding request" is ambiguous about which is the request, and
   * guessing produces a family tree upside down.
   */
  thisIsParent: z.boolean().default(true),
});
export type LinkTicketInput = z.infer<typeof linkTicketSchema>;

export const queueSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/),
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullish(),
  isDefault: z.boolean().default(false),
  sortOrder: z.number().int().default(0),
  active: z.boolean().default(true),
  /**
   * Only the queue's members (and each ticket's requester) can see its
   * tickets (072). Absent leaves it as it is, so a client that does not know
   * about it cannot open a restricted queue by saving it.
   */
  restricted: z.boolean().optional(),
  /** What belongs here, in words; mail intake routes matching requests here. */
  restrictedScope: z.string().trim().max(1000).nullish(),
  /**
   * Hand new unassigned tickets out automatically (073): in turn, or to
   * whoever has the fewest open. Absent leaves it as it is.
   */
  autoAssign: z.enum(['off', 'in_turn', 'least_open']).optional(),
  /** Who it hands work to; null = every agent and queue lead. Absent leaves it. */
  autoAssignPool: z.array(z.string().min(1).max(200)).max(200).nullable().optional(),
});
export type QueueInput = z.infer<typeof queueSchema>;

export const requestTypeSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,60}$/),
  label: z.string().min(1).max(120),
  /** FR-CL-03: sort order IS the ladder's evaluation priority. */
  sortOrder: z.number().int(),
  /**
   * Measured routing purity from the record, 0..1. Null means never measured —
   * which is not the same as zero, and the router treats them differently.
   */
  measuredPurity: z.number().min(0).max(1).nullish(),
  /**
   * What belongs under this type, in the desk's words. The model reads it to
   * choose the type by meaning; a type without one is left to the ladder.
   */
  description: z.string().trim().max(1000).nullish(),
  /**
   * This type's own acknowledgement, replacing the desk's for tickets of
   * this type (078). `{ticket}` becomes "ticket #123". Empty uses the desk's.
   */
  acknowledgement: z.string().trim().max(3000).nullish(),
  active: z.boolean().default(true),
});
export type RequestTypeInput = z.infer<typeof requestTypeSchema>;

/**
 * A rule edit. Every field is optional so the routing screen can reorder a
 * rule, or deactivate one, without resubmitting its whole predicate.
 */
export const routingRuleUpdateSchema = routingRuleSchema.partial().extend({
  active: z.boolean().optional(),
});
export type RoutingRuleUpdate = z.infer<typeof routingRuleUpdateSchema>;

export const businessCalendarSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/),
  timezone: z.string().min(3).max(60),
  hours: z
    .array(
      z.object({
        dow: z.number().int().min(1).max(7),
        start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
        end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
      }),
    )
    .max(21),
  holidays: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).max(400).default([]),
});
export type BusinessCalendarInput = z.infer<typeof businessCalendarSchema>;

/**
 * Tenant settings. A patch: only the keys sent are changed, because the
 * settings column is shared by screens that do not know about each other.
 */
/**
 * A RAG collection or source name.
 *
 * Lowercase, digits and underscore only. The server appends an environment
 * suffix to build the physical Qdrant collection, and a name carrying a dot,
 * a dash or a space produces a collection that is awkward to address and
 * impossible to guess from the logical name. Constrain it here, once, rather
 * than discovering it at the first push.
 */
export const ragNameSchema = z
  .string()
  .min(2)
  .max(60)
  .regex(/^[a-z][a-z0-9_]*$/, 'lowercase letters, digits and underscore; must start with a letter');

/**
 * Where one lane publishes (FR-VEC-01/02/04).
 *
 * `collection` is the assistant/domain — a physically separate Qdrant
 * collection. `source` is this tenant's namespace inside it, and is also the
 * unit a freshness sweep operates on, which is why it must be unique to this
 * tenant and this lane: two tenants sharing a source means one tenant's sync
 * deletes the other's articles.
 */
export const ragLaneSchema = z.object({
  /** Off by default. Nothing is ever published by a tenant nobody switched on. */
  enabled: z.boolean().default(false),
  collection: ragNameSchema,
  source: ragNameSchema,
});
export type RagLaneInput = z.infer<typeof ragLaneSchema>;

export const knowledgeSettingsSchema = z.object({
  /** Requester-facing. The intake assistant may auto-respond from this lane. */
  external: ragLaneSchema,
  /** Agent-facing suggestions. The assistant must never read this lane. */
  internal: ragLaneSchema,
  /**
   * How long an approved article stands before it must be reviewed again
   * (FR-KB-35). Past it the article is flagged, and past the grace period
   * it is demoted out of the requester-facing lane.
   */
  reviewAfterDays: z.number().int().min(30).max(1095).default(365),
  staleGraceDays: z.number().int().min(0).max(365).default(60),
  /**
   * How often pattern mining sweeps this desk, in days.
   *
   * Per group because the right cadence is a property of the desk, not the
   * deployment: a desk taking forty tickets a day has something new to find
   * daily, and one taking four does not — it would pay for a model run to
   * be told the same three patterns it was told yesterday.
   *
   * A FLOOR, not a timer. The sweep asks when this group was last done, so
   * a worker restart cannot re-run every group's paid merge on every deploy.
   */
  miningEveryDays: z.number().int().min(1).max(30).default(1),
  /**
   * How far back a sweep looks, in days.
   *
   * THE BOUND ON COST. Without it a sweep sends every distinct statement the
   * desk has ever produced to the model, every run, for ever — and a desk
   * carrying an imported history of tens of thousands of tickets pays for all
   * of it to be re-merged daily, to be told the same patterns it was told
   * yesterday.
   *
   * It is also the more honest question. "Problems the desk keeps solving"
   * means lately: a problem that stopped happening two years ago is not work
   * anybody is still doing, and proposing an article for it is noise. A
   * pattern that stops recurring drops off the list on its own, which is what
   * should happen to it.
   *
   * Long by default, because the first thing any desk does here is mine its
   * imported history and that history is the evidence. Shorten it once the
   * backlog has been written up.
   */
  miningLookbackDays: z.number().int().min(7).max(3650).default(365),
  /**
   * The suggestion floor, as a percentage. Below it nothing is offered.
   *
   * PER GROUP, because the right value is a property of the corpus rather
   * than of the code, and the corpora differ: a desk whose collection holds
   * real runbooks can afford a lower bar than one whose collection is still
   * mostly strategy documents. It moves as the knowledgebase improves, which
   * is a thing that happens between deployments, not at one.
   *
   * A PERCENTAGE rather than the 0-1 score the retriever returns, because
   * this is the one number in the system a person sets by looking at what
   * the panel showed them — and what the panel shows them is "62% match".
   * Two scales for one quantity is how somebody types 50 meaning half and
   * silently switches the feature off.
   *
   * Optional: unset means the deployment default (KB_SUGGEST_MIN_SCORE),
   * so the environment variable keeps meaning something rather than becoming
   * a setting nothing reads.
   */
  suggestMinPercent: z.number().int().min(0).max(100).optional(),
  /**
   * The same bar for what a REQUESTER is offered (the portal, the intake
   * assistant's search). Unset falls back to the agent bar above, then the
   * deployment default — one bar served both until they were split, and a
   * desk that set it should not find the requester side suddenly moved.
   */
  requesterSuggestMinPercent: z.number().int().min(0).max(100).optional(),
  /**
   * How far under the bar the model may look, in percentage points.
   *
   * Similarity shortlists; it cannot decide. The right article for a real
   * ticket scored 74% against a 75% bar and was thrown away before the
   * model that would have recognised it was ever asked. Within this margin
   * the model reads the article and decides; below it, nothing is read.
   * Without a model the bar is exact. Unset means
   * DEFAULT_JUDGE_MARGIN_PERCENT.
   */
  suggestJudgeMarginPercent: z.number().int().min(0).max(50).optional(),
  requesterSuggestJudgeMarginPercent: z.number().int().min(0).max(50).optional(),
});
export type KnowledgeSettingsInput = z.infer<typeof knowledgeSettingsSchema>;

/** How far under the suggestion bar the model reads, when a desk has not said. */
export const DEFAULT_JUDGE_MARGIN_PERCENT = 15;

/**
 * Platform settings — the RAG connection, shared by every group.
 *
 * A patch: only the keys sent are changed. `null` clears a value and falls
 * that field back to its environment variable, which is a different act from
 * leaving it alone and is carried through as such.
 */
const nullableTrimmed = (max: number) => z.string().trim().max(max).nullable();

/**
 * One MCP server's connection. The same four fields for every server — the
 * pattern is shared, the credentials never are.
 */
export const mcpServerPatchSchema = z
  .object({
    baseUrl: z
      .string()
      .trim()
      .max(500)
      .refine((v) => v === '' || /^https?:\/\//i.test(v), 'must be an http or https URL')
      .nullable(),
    clientId: nullableTrimmed(200),
    clientSecret: nullableTrimmed(500),
    tokenUrl: z
      .string()
      .trim()
      .max(500)
      .refine((v) => v === '' || /^https?:\/\//i.test(v), 'must be an http or https URL')
      .nullable(),
  })
  .partial();

export const platformSettingsSchema = z
  .object({
    /** Keyed by server key. Adding a server needs no change here. */
    servers: z.record(z.string().min(1).max(40), mcpServerPatchSchema),
    /** RAG's own settings, which no other server has. */
    ragEnvironment: nullableTrimmed(40),
    ragIngestToken: nullableTrimmed(500),
    /**
     * Where attachments live. Empty clears the value and falls the field back
     * to the environment, which is a deliberate act and different from
     * "unchanged" — so the two are carried separately, not collapsed.
     */
    storageAccount: nullableTrimmed(120),
    storageContainer: nullableTrimmed(120),
    /** Base folder, e.g. `qa`. Slashes are normalised server-side. */
    storagePrefix: nullableTrimmed(120),
    storageTenantId: nullableTrimmed(80),
    storageClientId: nullableTrimmed(80),
    storageClientSecret: nullableTrimmed(500),
  })
  .partial();
export type PlatformSettingsInput = z.infer<typeof platformSettingsSchema>;

export const tenantSettingsSchema = z
  .object({
    autoCloseDays: z.number().int().min(1).max(90),
    reopenWindowDays: z.number().int().min(1).max(365),
    /** Open decision #4. Below this the router refuses to guess. */
    autoRouteThreshold: z.number().min(0.5).max(1),
    showSlaToRequester: z.boolean(),
    allowOnBehalfOf: z.boolean(),
    /**
     * This desk takes requests from the assistant's front door
     * (`/api/v1/intake/*`): it is offered when the assistant decides whose a
     * question is, and the platform's intake key may file here for the person
     * who asked. Off unless the desk turns it on.
     */
    assistantIntake: z.boolean(),
    /**
     * This desk's mailbox. Empty string clears it, which falls the group back
     * to the deployment default rather than leaving it with no Reply-To.
     */
    deskEmail: z
      .string()
      .trim()
      .max(200)
      .refine((v) => v === '' || isMailboxSpec(v), {
        message: 'One address, as "Name <a@b>" or "a@b"',
      }),
    knowledge: knowledgeSettingsSchema,
    /** Outbound mail caps (058). Replaced whole: send all three, or omit. */
    outbound: outboundSettingsSchema,
    /** Intake assistant configuration. Replaced whole: send the full object. */
    intake: intakeSettingsSchema,
    /**
     * Which request type is a report of a suspicious message, call or link:
     * the one campaign detection groups. Its key or label; empty turns
     * campaign detection off for the group.
     */
    securityReportType: z.string().trim().max(120),
  })
  .partial();
export type TenantSettingsInput = z.infer<typeof tenantSettingsSchema>;

export const grantRoleSchema = z.object({
  email: esaEmailSchema,
  role: z.enum(TENANT_ROLES),
  reason: z.string().max(500).optional(),
});

export const simulateInboundSchema = z.object({
  /** Raw RFC-5322 message. The demo path for FR-EM-* (D-06). */
  raw: z.string().min(10).max(2_000_000).optional(),
  /** Or the parts, and we assemble a well-formed message. */
  from: z.string().max(320).optional(),
  to: z.string().max(320).optional(),
  subject: z.string().max(500).optional(),
  body: z.string().max(100_000).optional(),
  inReplyTo: z.string().max(400).optional(),
});

/**
 * The browser's telemetry POST.
 *
 * Deliberately shallow: `events` is left as `z.unknown()[]`, not an array of
 * a strict per-event schema. A strict schema rejects the WHOLE request the
 * moment one event is malformed, which turns one bad event — a stale build
 * sending a field this version renamed — into the batch's ninety-nine good
 * ones being lost with it. `telemetry.ts#ingestBatch` validates each event on
 * its own and drops only that one; the only thing enforced here is the batch
 * SIZE, because a batch over the shared limit is the client itself
 * misbehaving (looping, or not batching at all) and rejecting the whole
 * thing is the correct answer to that.
 */
export const telemetryBatchSchema = z.object({
  release: z.string().max(120).nullable().optional(),
  events: z.array(z.unknown()).max(TELEMETRY_LIMITS.maxEventsPerBatch),
});
export type TelemetryBatchInput = z.infer<typeof telemetryBatchSchema>;

/**
 * A person saying this is wrong.
 *
 * The comment is REQUIRED and everything else is optional, which is the
 * right way round: a report with no context is still a report, and one that
 * was refused because the browser could not supply a viewport is a bug
 * nobody ever hears about. The context is bounded rather than validated —
 * anything odd is worth keeping verbatim, since an odd value is itself
 * evidence.
 *
 * Nothing here carries page CONTENT. See services/bugs.ts for why the DOM is
 * deliberately not captured.
 */
export const bugReportSchema = z.object({
  comment: z.string().trim().min(1).max(4000),
  path: z.string().max(500).optional(),
  route: z.string().max(200).optional(),
  tenant: z.string().max(64).optional(),
  release: z.string().max(120).optional(),
  userAgent: z.string().max(400).optional(),
  viewport: z.string().max(60).optional(),
  theme: z.string().max(20).optional(),
  recent: z
    .array(
      z.object({
        kind: z.string().max(40),
        name: z.string().max(200),
        at: z.string().max(40),
        status: z.number().int().optional(),
      }),
    )
    .max(20)
    .optional(),
});
export type BugReportInput = z.infer<typeof bugReportSchema>;

export const telemetryIssueStateSchema = z.object({
  state: z.enum(['open', 'acknowledged', 'muted', 'resolved']),
  note: z.string().max(2000).optional(),
});
export type TelemetryIssueStateInput = z.infer<typeof telemetryIssueStateSchema>;
